import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Sparkle, Coins, ArrowRight, Crown, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DealPage = () => {
  const { dealCode } = useParams();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const res = await axios.get(`${API}/api/deals/${dealCode}`);
        setDeal(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Deal not found or expired');
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [dealCode]);

  const handleAccept = async () => {
    if (!user) {
      toast.error('Please log in or create an account first');
      return;
    }
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/api/deals/accept`, { deal_code: dealCode });
      setAccepted(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept deal');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Deal Unavailable</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/" className="px-6 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors">
            Go to LustLink
          </Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle weight="fill" className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Deal Accepted!</h1>
          <p className="text-slate-400 mb-2">You now have a <span className="text-blue-400 font-bold">{deal.revenue_share_pct}% revenue share</span> — {deal.duration_label}.</p>
          {deal.bonus_tokens > 0 && (
            <p className="text-amber-400 font-semibold mb-6">{deal.bonus_tokens} bonus tokens added to your account!</p>
          )}
          <Link to="/feed" className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors">
            Start Creating
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4" data-testid="deal-page">
      <div className="max-w-lg w-full">
        {/* Brand */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
            <Heart weight="fill" className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-white">LustLink</span>
        </div>

        {/* Deal Card */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500/10 via-slate-900 to-amber-500/10 border border-blue-500/20 p-8">
          <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <Crown weight="fill" className="w-6 h-6 text-amber-400" />
              <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Exclusive Deal</span>
            </div>

            <h1 className="font-heading text-3xl font-bold text-white mb-3">{deal.title}</h1>

            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-between p-4 bg-black/30 rounded-xl">
                <span className="text-slate-400">Revenue Share</span>
                <span className="text-3xl font-black text-blue-400">{deal.revenue_share_pct}%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-black/30 rounded-xl">
                <span className="text-slate-400">Duration</span>
                <span className="text-xl font-bold text-white">{deal.duration_label}</span>
              </div>
              {deal.bonus_tokens > 0 && (
                <div className="flex items-center justify-between p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                  <span className="text-amber-400 flex items-center gap-2"><Coins className="w-5 h-5" /> Bonus Tokens</span>
                  <span className="text-xl font-bold text-amber-400">+{deal.bonus_tokens}</span>
                </div>
              )}
              {deal.perks && (
                <div className="p-4 bg-black/30 rounded-xl">
                  <span className="text-slate-400 text-sm block mb-1">Perks</span>
                  <p className="text-white">{deal.perks}</p>
                </div>
              )}
            </div>

            {user ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full py-4 bg-blue-500 text-white rounded-full font-bold text-lg hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="accept-deal-btn"
              >
                {accepting ? (
                  <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkle weight="fill" className="w-5 h-5" />
                    Accept This Deal
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <Link
                  to={`/auth?mode=register&deal=${dealCode}`}
                  className="w-full py-4 bg-blue-500 text-white rounded-full font-bold text-lg hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                  data-testid="signup-for-deal-btn"
                >
                  Sign Up & Claim Deal
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
                <Link
                  to={`/auth?mode=login&deal=${dealCode}`}
                  className="w-full py-3 border border-white/10 text-slate-400 rounded-full font-medium text-center block hover:text-white hover:border-white/20 transition-colors"
                >
                  Already have an account? Log in
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealPage;
